import request from '@/config/axios'

export interface AgentLevel {
  id: number
  code: string
  name: string
  rankNo: number
  qualificationFee: number
  firstDiscount: number
  renewDiscount: number
  oemEnabled: boolean
  customDomainEnabled: boolean
  dividendEnabled: boolean
  status: number
}
export interface Agent {
  id?: number
  userId: number
  levelId: number
  name: string
  contactName: string
  contactMobile: string
  authorizedRegion?: string
  dividendWeight?: number
  status?: number
}
export interface AgentProduct {
  id?: number
  name: string
  moduleCode: string
  firstPrice: number
  renewPrice: number
  durationMonths: number
  dividendEligible: boolean
  status: number
  sort: number
}
export interface AgentCustomer {
  id: number
  tenantId: number
  customerName: string
  contactName?: string
  contactMobile?: string
}
export interface AgentOrder {
  id: number
  customerId: number
  productId: number
  productName: string
  moduleCode: string
  orderType: number
  originalAmount: number
  discountRate: number
  settlementAmount: number
  customerAmount?: number
  payStatus: number
  fulfillStatus: number
  createTime: string
}

export interface AgentBrand {
  id?: number
  agentId?: number
  brandName: string
  logoUrl?: string
  loginLogoUrl?: string
  copyrightText?: string
  contactInfo?: string
  status?: number
  auditRemark?: string
}

export const getLevels = (): Promise<AgentLevel[]> =>
  request.get({ url: '/agent-mall/agent/level/list' })
export const createLevel = (data: Omit<AgentLevel, 'id'>) =>
  request.post({ url: '/agent-mall/agent/level/create', data })
export const updateLevel = (data: AgentLevel) =>
  request.put({ url: '/agent-mall/agent/level/update', data })
export const getAgents = (): Promise<Agent[]> => request.get({ url: '/agent-mall/agent/list' })
export const saveAgent = (data: Agent) => request.post({ url: '/agent-mall/agent/save', data })
export const auditAgent = (data: { id: number; approved: boolean; remark?: string }) =>
  request.post({ url: '/agent-mall/agent/audit', data })
export const getProducts = (admin = false): Promise<AgentProduct[]> =>
  request.get({ url: '/agent-mall/agent/product/list', params: { admin } })
export const saveProduct = (data: AgentProduct) =>
  request.post({ url: '/agent-mall/agent/product/save', data })
export const getMyProfile = (): Promise<Agent> => request.get({ url: '/agent-mall/agent/my/profile' })
export const getCustomers = (): Promise<AgentCustomer[]> =>
  request.get({ url: '/agent-mall/agent/my/customer/list' })
export const saveCustomer = (data: Partial<AgentCustomer>) =>
  request.post({ url: '/agent-mall/agent/my/customer/save', data })
export const previewOrder = (data: {
  customerId: number
  productId: number
  customerAmount?: number
}): Promise<AgentOrder> => request.post({ url: '/agent-mall/agent/my/order/preview', data })
export const createOrder = (data: {
  customerId: number
  productId: number
  customerAmount?: number
}): Promise<number> => request.post({ url: '/agent-mall/agent/my/order/create', data })
export const getOrderStatus = (id: number): Promise<AgentOrder> =>
  request.get({ url: '/agent-mall/agent/my/order/status', params: { id } })
export const getMyOrders = (): Promise<AgentOrder[]> =>
  request.get({ url: '/agent-mall/agent/my/order/list' })
export const getOrders = (): Promise<AgentOrder[]> =>
  request.get({ url: '/agent-mall/agent/order/list' })
export const confirmOrderPaid = (id: number) =>
  request.post({ url: '/agent-mall/agent/order/confirm-paid', params: { id } })
export const refundAgentOrder = (data: { orderId: number; reason: string }) =>
  request.post({ url: '/agent-mall/agent/order/refund', data })
export const getAgentRefunds = (orderId?: number) =>
  request.get({ url: '/agent-mall/agent/order/refund/list', params: { orderId } })
export const confirmAgentExternalRefund = (id: number) =>
  request.post({ url: '/agent-mall/agent/order/refund/confirm-external', params: { id } })
export const getMyBrand = (): Promise<AgentBrand | null> =>
  request.get({ url: '/agent-mall/agent/my/brand' })
export const saveMyBrand = (data: AgentBrand) =>
  request.post({ url: '/agent-mall/agent/my/brand/save', data })
export const resolveBrand = (tenantId: number): Promise<AgentBrand | null> =>
  request.get({
    url: '/agent-mall/agent/brand/resolve',
    params: { tenantId },
    headers: { isToken: false }
  })
export const getDividendBatches = () => request.get({ url: '/agent-mall/agent/dividend/batch/list' })
export const generateDividend = (data: object) =>
  request.post({ url: '/agent-mall/agent/dividend/generate', data })
export const getMyDividends = () => request.get({ url: '/agent-mall/agent/my/dividend/list' })
export const createQualification = (data: object) =>
  request.post({ url: '/agent-mall/agent/my/qualification/create', data })
export const getMyQualifications = () => request.get({ url: '/agent-mall/agent/my/qualification/list' })
export const getQualifications = () => request.get({ url: '/agent-mall/agent/qualification/list' })
export const reviewQualification = (data: object) =>
  request.post({ url: '/agent-mall/agent/qualification/review', data })
export const createDomain = (data: object) =>
  request.post({ url: '/agent-mall/agent/my/domain/create', data })
export const getDomains = (customerId: number) =>
  request.get({ url: '/agent-mall/agent/my/domain/list', params: { customerId } })
export const auditBrand = (data: object) => request.post({ url: '/agent-mall/agent/brand/audit', data })
export const updateDividendStatus = (id: number, status: number) =>
  request.post({ url: '/agent-mall/agent/dividend/status', params: { id, status } })

export interface AgentWallet {
  id: number
  agentId: number
  availableBalance: number
  frozenBalance: number
  pendingBalance: number
}

export interface AgentWithdrawal {
  id: number
  agentId: number
  amount: number
  accountName: string
  accountNo: string
  status: number
  auditRemark?: string
  createTime: string
}

export interface AgentCatalogItem {
  id?: number
  itemType: 'BASE_PRODUCT' | 'PLUGIN' | 'TEMPLATE'
  name: string
  categoryPath?: string
  moduleCode?: string
  version?: string
  previewUrl?: string
  priceMonth?: number
  priceQuarter?: number
  priceYear?: number
  priceLifetime?: number
  trialDays?: number
  minLevelRank?: number
  visible?: boolean
  status: number
  sort?: number
  description?: string
}

export const getMyWallet = (): Promise<AgentWallet> =>
  request.get({ url: '/agent-mall/agent-center/wallet/my' })
export const getMyWalletTransactions = () =>
  request.get({ url: '/agent-mall/agent-center/wallet/my-transactions' })
export const adjustWallet = (data: {
  agentId: number
  amount: number
  bizType: string
  remark?: string
}) => request.post({ url: '/agent-mall/agent-center/wallet/adjust', data })
export const createAgentRecharge = (data: {
  amount: number
}): Promise<{ id: number; payOrderId: number; status: number }> =>
  request.post({ url: '/agent-mall/agent-center/wallet/recharge/create', data })
export const getAgentRechargeStatus = (id: number) =>
  request.get({ url: '/agent-mall/agent-center/wallet/recharge/status', params: { id } })
export const createAgentWithdrawal = (data: {
  amount: number
  accountName: string
  accountNo: string
}) => request.post({ url: '/agent-mall/agent-center/wallet/withdrawal/create', data })
export const getMyAgentWithdrawals = (): Promise<AgentWithdrawal[]> =>
  request.get({ url: '/agent-mall/agent-center/wallet/withdrawal/my-list' })
export const getAgentWithdrawals = (): Promise<AgentWithdrawal[]> =>
  request.get({ url: '/agent-mall/agent-center/wallet/withdrawal/list' })
export const auditAgentWithdrawal = (data: { id: number; approved: boolean; remark?: string }) =>
  request.post({ url: '/agent-mall/agent-center/wallet/withdrawal/audit', data })
export const confirmAgentWithdrawalPaid = (id: number) =>
  request.post({ url: '/agent-mall/agent-center/wallet/withdrawal/confirm-paid', params: { id } })
export const getAgentRegions = (agentId?: number) =>
  request.get({ url: '/agent-mall/agent-center/region/list', params: { agentId } })
export const grantAgentRegion = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/region/grant', data })
export const assignCustomerByRegion = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/region/assign-customer', data })
export const transferAgentCustomer = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/customer/transfer', data })
export const getAgentCustomerTransferLogs = (tenantId?: number) =>
  request.get({ url: '/agent-mall/agent-center/customer/transfer-logs', params: { tenantId } })
export const getAgentCatalog = (): Promise<AgentCatalogItem[]> =>
  request.get({ url: '/agent-mall/agent-center/catalog/list' })
export const getAgentManagementCatalog = (): Promise<AgentCatalogItem[]> =>
  request.get({ url: '/agent-mall/agent-center/catalog/management-list' })
export const saveAgentCatalog = (data: AgentCatalogItem) =>
  request.post({ url: '/agent-mall/agent-center/catalog/save', data })
export const purchaseAgentCatalogItem = (data: {
  tenantId: number
  catalogItemId: number
  billingCycle: 'MONTH' | 'QUARTER' | 'YEAR' | 'LIFETIME'
}) => request.post({ url: '/agent-mall/agent-center/catalog/purchase', data })
export const getMyAgentCatalogOrders = () =>
  request.get({ url: '/agent-mall/agent-center/catalog/my-orders' })
export const createAgentCatalogRelease = (data: { catalogItemId: number; tenantIds: number[] }) =>
  request.post({ url: '/agent-mall/agent-center/catalog/release/create', data })
export const executeAgentCatalogRelease = (id: number) =>
  request.post({ url: '/agent-mall/agent-center/catalog/release/execute', params: { id } })
export const rollbackAgentCatalogRelease = (id: number) =>
  request.post({ url: '/agent-mall/agent-center/catalog/release/rollback', params: { id } })
export const getAgentCatalogReleaseTasks = (catalogItemId?: number) =>
  request.get({ url: '/agent-mall/agent-center/catalog/release/list', params: { catalogItemId } })
export const getMyTemplateAuthorizations = () =>
  request.get({ url: '/agent-mall/agent-center/template/my' })
export const authorizeTemplate = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/template/authorize', data })
export const upgradeAgentTemplate = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/template/upgrade', data })
export const rollbackAgentTemplate = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/template/rollback', data })
export const getMyAgentMembers = () => request.get({ url: '/agent-mall/agent-center/member/my' })
export const saveAgentMember = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/member/save', data })
export const getMyAdvertisements = () =>
  request.get({ url: '/agent-mall/agent-center/advertisement/my' })
export const getAgentAdvertisements = () =>
  request.get({ url: '/agent-mall/agent-center/advertisement/list' })
export const saveAgentAdvertisement = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/advertisement/save', data })
export const auditAgentAdvertisement = (data: object) =>
  request.post({ url: '/agent-mall/agent-center/advertisement/audit', data })
export const resolveAgentAdvertisements = (data: {
  placement: string
  agentId?: number
  tenantId?: number
}) => request.post({ url: '/agent-mall/agent-center/advertisement/resolve', data })
export const trackAgentAdvertisementEvent = (data: {
  advertisementId: number
  tenantId?: number
  eventType: 'IMPRESSION' | 'CLICK' | 'CONVERSION'
  visitorId?: string
  source?: string
}) => request.post({ url: '/agent-mall/agent-center/advertisement/event', data })
